import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Word } from '../../types/word.types';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Check, X, RotateCcw, Trash2, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loading } from '@/components/ui/loading';
import BackToPracticeButton from '../../components/ui/back-to-practice-button';
import { PageContainer } from '../../components/PageContainer';
import { getQuizById, generateQuiz, markQuizCompleted, type QuizResponse } from '../../services/quizService';
import { wordSearchService } from '../../services/wordSearchService';
import { userWordsService } from '../../services/userWordsService';
import WordListItem from '../../components/LearnWords/WordListItem';

// Words will be passed via navigation state

interface BlankItem {
  id: string;
  correctWord: string;
  userAnswer: string | null;
}

function FillInTheBlankPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Quiz state
  const [quizData, setQuizData] = useState<QuizResponse['data'] | null>(null);
  const [isLoadingQuiz, setIsLoadingQuiz] = useState(true);
  const [quizError, setQuizError] = useState<string | null>(null);

  // Game state
  const [blanks, setBlanks] = useState<BlankItem[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionResults, setSubmissionResults] = useState<boolean[]>([]);
  const [selectedWordForDetails, setSelectedWordForDetails] = useState<Word | null>(null);

  // Clickable word state
  const [clickedWord, setClickedWord] = useState<string | null>(null);
  const [wordDefinition, setWordDefinition] = useState<{
    word: string;
    data: Array<{
      phonetic?: string;
      meanings: Array<{
        partOfSpeech: string;
        definitions: Array<{ definition: string }>;
      }>;
    }>;
  } | null>(null);
  const [isLoadingDefinition, setIsLoadingDefinition] = useState(false);
  const [isAddingWord, setIsAddingWord] = useState(false);
  const [showFullDefinition, setShowFullDefinition] = useState(false);
  const [addedWords, setAddedWords] = useState<Set<string>>(new Set());

  // Load quiz by ID on mount
  useEffect(() => {
    if (!id) return;

    const loadQuiz = async () => {
      setIsLoadingQuiz(true);
      setQuizError(null);

      const response = await getQuizById(id);

      if (response.ok && response.data) {
        setQuizData(response.data);
        // Initialize blanks based on the number of answers
        const initialBlanks = response.data.answer.map((answer, index) => ({
          id: `blank-${index}`,
          correctWord: answer,
          userAnswer: null,
        }));
        setBlanks(initialBlanks);
        setSubmissionResults([]);
      } else {
        setQuizError('Failed to load quiz');
      }

      setIsLoadingQuiz(false);
    };

    loadQuiz();
  }, [id]);


  // Parse paragraph from API response
  const { paragraph } = useMemo(() => {
    if (!quizData) {
      return { paragraph: [] };
    }

    // Parse the paragraph with blanks
    const parts: Array<{ type: 'text' | 'blank'; content: string; blankId?: string }> = [];
    const blankPattern = /__(\d+)__/g;
    let lastIndex = 0;
    let match;

    while ((match = blankPattern.exec(quizData.paragraph)) !== null) {
      // Add text before blank
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: quizData.paragraph.substring(lastIndex, match.index),
        });
      }

      // Add blank
      const blankNum = parseInt(match[1]) - 1;
      if (blankNum < quizData.answer.length) {
        const blankId = `blank-${blankNum}`;
        parts.push({
          type: 'blank',
          content: '',
          blankId,
        });
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < quizData.paragraph.length) {
      parts.push({
        type: 'text',
        content: quizData.paragraph.substring(lastIndex),
      });
    }

    return { paragraph: parts };
  }, [quizData]);

  const handleAnswerChange = (blankId: string, answer: string) => {
    setBlanks(prev =>
      prev.map(blank =>
        blank.id === blankId ? { ...blank, userAnswer: answer } : blank
      )
    );
  };

  const handleSubmit = () => {
    // Record which answers were correct at submission time
    const results = blanks.map(blank =>
      blank.userAnswer?.toLowerCase() === blank.correctWord.toLowerCase()
    );
    setSubmissionResults(results);
    setIsSubmitted(true);
  };

  // Mark quiz as completed when user gets all answers correct
  useEffect(() => {
    const markCompleted = async () => {
      const currentCorrectCount = blanks.filter(
        blank => blank.userAnswer?.toLowerCase() === blank.correctWord.toLowerCase()
      ).length;

      if (isSubmitted && currentCorrectCount === blanks.length && id) {
        try {
          await markQuizCompleted(id);
          // Optionally show a success message or update UI
        } catch (error) {
          // Silently fail - completion status is not critical for UX
          console.error('Failed to mark quiz as completed:', error);
        }
      }
    };

    markCompleted();
  }, [isSubmitted, blanks, id]);

  const handleReset = () => {
    // Keep correct answers prefilled, reset incorrect ones
    setBlanks(prev =>
      prev.map((blank, index) => ({
        ...blank,
        userAnswer: submissionResults[index] ? blank.correctWord : null
      }))
    );
    setIsSubmitted(false);
  };

  const handleRegenerate = async () => {
    if (!quizData) return;

    setIsLoadingQuiz(true);
    setQuizError(null);
    setIsSubmitted(false);
    setSubmissionResults([]);

    // Extract words from current quiz data
    const words = Object.keys(quizData.words);
    const response = await generateQuiz(words);

    if (response.ok && response.data?.id) {
      // Navigate to the new quiz
      navigate(`/practice/fill-in-the-blank/${response.data.id}`);
    } else {
      setQuizError('Failed to generate new quiz');
      setIsLoadingQuiz(false);
    }
  };

  // Handle clicking on a word in the paragraph
  const handleWordClick = async (word: string) => {
    setClickedWord(word);
    setIsLoadingDefinition(true);
    setWordDefinition(null);
    setShowFullDefinition(false);

    try {
      const result = await wordSearchService.searchWord(word);
      setWordDefinition(result);
    } catch (error) {
      console.error('Failed to fetch word definition:', error);
      setWordDefinition(null);
    } finally {
      setIsLoadingDefinition(false);
    }
  };

  // Handle adding word to collection
  const handleAddToCollection = async () => {
    if (!clickedWord) return;

    setIsAddingWord(true);
    try {
      await userWordsService.addWord(clickedWord);
      // Mark word as added in this session
      setAddedWords(prev => new Set(prev).add(clickedWord.toLowerCase()));
      // Close popover after successful add
      setClickedWord(null);
      setWordDefinition(null);
    } catch (error) {
      console.error('Failed to add word:', error);
    } finally {
      setIsAddingWord(false);
    }
  };


  const allAnswered = blanks.every(blank => blank.userAnswer !== null);
  const correctCount = blanks.filter(
    blank => blank.userAnswer?.toLowerCase() === blank.correctWord.toLowerCase()
  ).length;

  // Extract real word objects from quiz data
  const wordObjects = useMemo(() => {
    if (!quizData?.words) return [];

    return Object.entries(quizData.words).map(([wordKey, wordData]) => {
      const apiWord = wordData.data[0]; // Take first definition

      // Convert API format to our Word format
      const definitions = apiWord.meanings.map(meaning => ({
        partOfSpeech: meaning.partOfSpeech,
        meaning: meaning.definitions[0]?.definition || 'No definition available',
        example: meaning.definitions[0]?.example,
        synonyms: meaning.synonyms || [],
        antonyms: meaning.antonyms || [],
        definitions: meaning.definitions.map(def => ({
          definition: def.definition,
          example: def.example,
          synonyms: def.synonyms || [],
          antonyms: def.antonyms || [],
        })),
      }));

      return {
        id: `quiz-${wordKey}`,
        word: apiWord.word,
        definitions,
        addedAt: new Date(),
        rating: 3,
        phonetic: apiWord.phonetic,
        phonetics: apiWord.phonetics,
        sourceUrls: apiWord.sourceUrls,
      };
    });
  }, [quizData]);

  // Words for dropdown options - sorted alphabetically
  const dropdownWords = useMemo(() => {
    return wordObjects
      .map(word => word.word)
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [wordObjects]);

  // Track which words are used
  const usedWords = useMemo(() => {
    return new Set(blanks.map(blank => blank.userAnswer).filter(Boolean));
  }, [blanks]);

  // Handle missing quiz ID
  if (!id) {
    return (
      <PageContainer>
        <div className="max-w-4xl mx-auto space-y-4 md:space-y-6">
          <BackToPracticeButton showChangeWords={true} />
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">No quiz ID provided.</p>
              <p className="text-sm text-muted-foreground mt-2">
                Try selecting words to start a new quiz.
              </p>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    );
  }

  // Show loading state
  if (isLoadingQuiz) {
    return (
      <PageContainer>
        <div className="max-w-4xl mx-auto space-y-4 md:space-y-6">
          <BackToPracticeButton showChangeWords={true} />
          <Loading size="lg" message="Generating your quiz..." />
        </div>
      </PageContainer>
    );
  }

  // Show error state
  if (quizError) {
    return (
      <PageContainer>
        <div className="max-w-4xl mx-auto space-y-4 md:space-y-6">
          <BackToPracticeButton showChangeWords={true} />
          <Card>
            <CardContent className="p-12">
              <div className="text-center text-muted-foreground">
                <X className="h-12 w-12 mx-auto mb-4 text-red-500" />
                <p className="text-lg mb-2">Failed to generate quiz</p>
                <p className="text-sm">{quizError}</p>
                <Button
                  onClick={() => window.location.reload()}
                  className="mt-4"
                  variant="outline"
                >
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto space-y-4 md:space-y-6">
      {/* Header */}
      <div className="mb-2 md:mb-3">
        <BackToPracticeButton showChangeWords={true} />
      </div>

      <div className="mb-4 md:mb-6">
        <div className="flex flex-col space-y-4 md:flex-row md:items-start md:justify-between md:space-y-0">
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold mb-2">Fill in the Blank</h1>
            <p className="text-muted-foreground text-sm md:text-base">
              Complete the paragraph by selecting the correct words from the dropdowns
            </p>
          </div>
        </div>
      </div>

          {/* Progress */}
          {isSubmitted && (
            <Card className="mb-4 md:mb-6">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Your Score</p>
                    <div className="flex items-baseline gap-2">
                      <p className="text-3xl font-bold">{correctCount}</p>
                      <p className="text-lg text-muted-foreground">/ {blanks.length}</p>
                    </div>
                  </div>
                  <div className={cn(
                    "text-4xl font-bold",
                    correctCount === blanks.length ? "text-green-600" : "text-orange-600"
                  )}>
                    {Math.round((correctCount / blanks.length) * 100)}%
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

      {/* Word Bank Reference */}
      {paragraph.length > 0 && (
        <Card className="mt-3">
        <CardContent className="p-3 md:p-4">
          <div className="mb-3">
            <h3 className="text-sm md:text-base font-semibold">Word Bank</h3>
            <p className="text-xs text-muted-foreground">
              Click any word for details
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {wordObjects.sort((a, b) => a.word.toLowerCase().localeCompare(b.word.toLowerCase())).map((word) => (
              <button
                key={word.id}
                onClick={() => setSelectedWordForDetails(word)}
                className="px-3 py-1.5 text-sm bg-muted hover:bg-muted/80 rounded-full border transition-colors"
              >
                {word.word}
              </button>
            ))}
          </div>

          {/* Word Details Modal */}
          <Dialog open={!!selectedWordForDetails} onOpenChange={() => setSelectedWordForDetails(null)}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden p-0">
              <div className="p-6 pb-4">
              </div>
              <div className="px-6 pb-6 max-h-[60vh] overflow-y-auto">
                {selectedWordForDetails && (
                  <WordListItem
                    word={selectedWordForDetails}
                    onDelete={() => {}} // No-op for preview mode
                    onRatingChange={() => {}} // No-op for preview mode
                    preview={true}
                  />
                )}
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
      )}

      {/* Paragraph with Blanks */}
      {paragraph.length > 0 && (
        <Card className="mt-3">
        <CardContent className="p-4 md:p-8">
            <div className="text-base md:text-lg space-y-3 md:space-y-4">
            <p className="leading-[2.5rem] md:leading-[3rem]">
              {paragraph.length > 0 ? paragraph.map((part, index) => {
                if (part.type === 'text') {
                  // Split text into words and make them clickable
                  const words = part.content.split(/(\s+|[.,!?;:])/);
                  return (
                    <span key={index}>
                      {words.map((segment, segIndex) => {
                        // Check if it's a word (not whitespace or punctuation)
                        const isWord = /^[a-zA-Z]+$/.test(segment);
                        const isAdded = addedWords.has(segment.toLowerCase());
                        if (isWord) {
                          return (
                            <Popover key={segIndex} open={clickedWord === segment} onOpenChange={(open) => !open && setClickedWord(null)}>
                              <PopoverTrigger asChild>
                                <button
                                  onClick={() => handleWordClick(segment)}
                                  className={cn(
                                    "hover:text-primary hover:underline decoration-2 underline-offset-2 transition-colors cursor-pointer",
                                    isAdded && "text-green-600 font-semibold"
                                  )}
                                >
                                  {segment}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-96 p-0" align="center" side="top" sideOffset={8} alignOffset={0}>
                                <div className="p-4">
                                  {isLoadingDefinition ? (
                                    <div className="flex items-center justify-center py-8">
                                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                    </div>
                                  ) : wordDefinition ? (
                                    <div className="space-y-3">
                                      <div className="flex items-start justify-between gap-3">
                                        <h4 className="font-bold text-lg pt-1">{wordDefinition.word}</h4>
                                        <div className="flex items-center gap-2">
                                          <Button
                                            size="default"
                                            onClick={handleAddToCollection}
                                            disabled={isAddingWord}
                                            className="gap-2 min-w-[100px]"
                                          >
                                            {isAddingWord ? (
                                              <>
                                                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                Adding...
                                              </>
                                            ) : (
                                              <>
                                                <Plus className="w-4 h-4" />
                                                Add Word
                                              </>
                                            )}
                                          </Button>
                                          <Button
                                            size="icon"
                                            variant="outline"
                                            onClick={() => setClickedWord(null)}
                                            className="h-10 w-10 shrink-0"
                                            title="Close"
                                          >
                                            <X className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </div>
                                      {wordDefinition.data?.[0]?.phonetic && (
                                        <p className="text-sm text-muted-foreground">{wordDefinition.data[0].phonetic}</p>
                                      )}
                                      <div className={cn(
                                        "space-y-3 overflow-y-auto",
                                        showFullDefinition ? "max-h-96" : "max-h-48"
                                      )}>
                                        {(showFullDefinition 
                                          ? wordDefinition.data?.[0]?.meanings 
                                          : wordDefinition.data?.[0]?.meanings?.slice(0, 2)
                                        )?.map((meaning, idx: number) => (
                                          <div key={idx} className="space-y-2 pb-2 border-b last:border-b-0">
                                            <p className="text-xs font-semibold text-muted-foreground uppercase">{meaning.partOfSpeech}</p>
                                            {(showFullDefinition 
                                              ? meaning.definitions 
                                              : meaning.definitions?.slice(0, 1)
                                            )?.map((def, defIdx: number) => (
                                              <div key={defIdx} className="space-y-1">
                                                <p className="text-sm leading-relaxed">
                                                  <span className="font-medium text-foreground">{defIdx + 1}.</span> {def.definition}
                                                </p>
                                              </div>
                                            ))}
                                          </div>
                                        ))}
                                      </div>
                                      {wordDefinition.data?.[0]?.meanings && wordDefinition.data[0].meanings.length > 2 && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => setShowFullDefinition(!showFullDefinition)}
                                          className="w-full gap-1.5 text-xs"
                                        >
                                          {showFullDefinition ? 'Show Less' : `Show More`}
                                        </Button>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="py-4 text-center text-sm text-muted-foreground">
                                      Failed to load definition
                                    </div>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          );
                        }
                        return <span key={segIndex}>{segment}</span>;
                      })}
                    </span>
                  );
                } else {
                  const blank = blanks.find(b => b.id === part.blankId);
                  if (!blank) return null;

                  const isCorrect = blank.userAnswer?.toLowerCase() === blank.correctWord.toLowerCase();
                  const showResult = isSubmitted && blank.userAnswer;

                  return (
                    <span key={index} className="inline-flex items-center mx-1 my-2">
                      <Select
                        value={blank.userAnswer || ''}
                        onValueChange={(value) => handleAnswerChange(blank.id, value)}
                        disabled={isSubmitted}
                      >
                        <SelectTrigger
                          className={cn(
                            "w-[140px] sm:w-[160px] md:w-[200px] inline-flex transition-all",
                            blank.userAnswer === null && !isSubmitted && "border-dashed border-2 border-muted-foreground/50",
                            blank.userAnswer !== null && !isSubmitted && "border-primary/50 bg-primary/5",
                            showResult && (isCorrect ? "border-green-500 bg-green-50" : "border-red-500 bg-red-50")
                          )}
                        >
                          <SelectValue placeholder="Select word...">
                            {blank.userAnswer || "Select word..."}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {dropdownWords.map((word) => {
                            const isUsed = usedWords.has(word);
                            return (
                              <SelectItem
                                key={word}
                                value={word}
                                disabled={isUsed}
                                className="flex items-center justify-between"
                              >
                                <span className={cn(isUsed && "text-muted-foreground")}>{word}</span>
                                {isUsed && (
                                  <span className="text-xs font-medium text-green-600 ml-auto pl-2">used</span>
                                )}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {showResult && (
                        <span className="ml-2">
                          {isCorrect ? (
                            <Check className="h-5 w-5 text-green-600" />
                          ) : (
                            <X className="h-5 w-5 text-red-600" />
                          )}
                        </span>
                      )}
                    </span>
                  );
                }
              }) : (
                <span className="text-muted-foreground">Loading quiz...</span>
              )}
            </p>

            {/* Quiz Controls */}
            {!isSubmitted && (
              <div className="mt-6 pt-4 border-t">
                <div className="flex items-center justify-end mb-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReset}
                      className="gap-1"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Clear</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRegenerate}
                      disabled={isLoadingQuiz}
                      className="gap-1"
                    >
                      {isLoadingQuiz ? (
                        <>
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <RotateCcw className="h-4 w-4" />
                          <span>Regenerate</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <Button
                  onClick={handleSubmit}
                  disabled={!allAnswered}
                  className="w-full"
                  size="lg"
                >
                  Check Answers
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      )}



          {/* Try Again Button */}
          {isSubmitted && correctCount < blanks.length && (
            <div className="mt-8 flex justify-center">
              <Button
                size="lg"
                onClick={handleReset}
              >
                Try Again
              </Button>
            </div>
          )}

      {/* Success Message */}
      {isSubmitted && correctCount === blanks.length && (
        <Card className="mt-6 md:mt-8 border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/20">
          <CardContent className="p-6 md:p-8 text-center">
                <div className="mb-4 md:mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 bg-green-100 rounded-full mb-4 dark:bg-green-900">
                    <Check className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-green-800 mb-2 dark:text-green-400">
                    Excellent Work! 🎉
                  </h3>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <BackToPracticeButton />
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleRegenerate}
                    disabled={isLoadingQuiz}
                    className="border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-300 dark:hover:bg-green-950"
                  >
                    {isLoadingQuiz ? (
                      <>
                        <div className="w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Regenerate
                      </>
                    )}
                  </Button>
                </div>
          </CardContent>
        </Card>
      )}

      </div>
    </PageContainer>
  );
}

export default FillInTheBlankPage;
